import axios from "axios";
import {
  LoginCredentials,
  AuthResponse,
  User,
  ChangePasswordRequest,
} from "../types/auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// Create axios instance for auth
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle auth errors
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token on 401
      localStorage.removeItem("authToken");
      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export const authService = {
  // Login
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await authApi.post<AuthResponse>(
      "/auth/login",
      credentials,
    );
    return response.data;
  },

  // Get user profile
  getProfile: async (): Promise<User> => {
    const response = await authApi.get<User>("/auth/profile");
    return response.data;
  },

  // Change password
  changePassword: async (
    passwordData: ChangePasswordRequest,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await authApi.put<{ success: boolean; message: string }>(
      "/auth/change-password",
      passwordData,
    );
    return response.data;
  },

  // Verify token
  verifyToken: async (): Promise<boolean> => {
    try {
      await authApi.get("/auth/verify");
      return true;
    } catch (error) {
      return false;
    }
  },

  // Logout (client-side only, server doesn't need to be called)
  logout: () => {
    localStorage.removeItem("authToken");
  },
};
